import { Card, CardContent } from "@/components/ui/card";
import type { MasterControlWorkflowEntry } from "@/hooks/useMasterControl";

type MCWorkflowSummaryCardsProps = {
  stats?: Record<string, number>;
  active: MasterControlWorkflowEntry[];
  stalled: MasterControlWorkflowEntry[];
  pending: MasterControlWorkflowEntry[];
};

export function MCWorkflowSummaryCards({ stats, active, stalled, pending }: MCWorkflowSummaryCardsProps) {
  const avgRuntime = (() => {
    const durations = active.map((entry) => entry.run_time_seconds ?? 0).filter(Boolean);
    if (!durations.length) return "—";
    const average = durations.reduce((sum, value) => sum + value, 0) / durations.length;
    return `${Math.round(average)}s`;
  })();

  const longestStalled = (() => {
    const stalledDurations = stalled.map((entry) => entry.stalled_for_seconds ?? 0).filter(Boolean);
    if (!stalledDurations.length) return "—";
    return `${Math.max(...stalledDurations)}s`;
  })();

  const allEntries = [...active, ...stalled, ...pending];
  const lastTimestamp = (() => {
    const timestamps = allEntries
      .map((entry) => entry.last_event_at)
      .filter(Boolean)
      .map((value) => Date.parse(value as string))
      .filter((value) => Number.isFinite(value));
    if (!timestamps.length) return "—";
    return new Date(Math.max(...timestamps)).toLocaleString();
  })();

  const cards = [
    {
      label: "Active workflows",
      value: stats?.active_runs ?? active.length,
    },
    {
      label: "Stalled workflows",
      value: stats?.stalled_runs ?? stalled.length,
    },
    {
      label: "Pending tasks",
      value: stats?.pending_tasks ?? pending.length,
    },
    {
      label: "Avg runtime",
      value: avgRuntime,
    },
    {
      label: "Longest stalled",
      value: longestStalled,
    },
    {
      label: "Last workflow event",
      value: lastTimestamp,
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {cards.map((card) => (
        <Card key={card.label} className="rounded-2xl border border-border bg-background/60 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold text-foreground">{card.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default MCWorkflowSummaryCards;
